import { Form, normalizeFormResponse } from "../../core/Form";
import { Toast } from "../../utils/Toast";

const PHONE_PATTERN = /^0\d{1,2}-?\d{3,4}-?\d{4}$/;

/**
 * 모바일 전용 하단 "간편 문의" bar 의 AJAX 제출 + 높이 노출.
 * 마크업: resources/views/components/quick-contact-bar.php
 * 제출: core/Form → POST about.contact.quick (ContactController::quickSubmit).
 * --quick-bar-h: 모바일에서만 bar 실제 높이를 루트 변수로 노출(.__root 하단 여백 · 플로팅버튼 오프셋 공유).
 */
export class QuickContactBarController {
    static init(): void {
        const bar = document.querySelector<HTMLElement>("[data-quick-contact-bar]");
        if (!bar) return;

        const openButton = bar.querySelector<HTMLButtonElement>("[data-quick-contact-open]");
        const closeButton = bar.querySelector<HTMLButtonElement>("[data-quick-contact-close]");
        const panel = bar.querySelector<HTMLElement>("[data-quick-contact-panel]");
        const mobileQuery = window.matchMedia("(max-width: 767px)");

        const isOpen = (): boolean => bar.classList.contains("is-open");

        const updateViewportMetrics = (): void => {
            if (!mobileQuery.matches) {
                document.documentElement.style.setProperty("--quick-bar-h", "0px");
                document.documentElement.style.removeProperty("--quick-vv-h");
                document.documentElement.style.removeProperty("--quick-vv-bottom");
                return;
            }

            const visualViewport = window.visualViewport;
            const viewportHeight = Math.max(1, Math.round(visualViewport?.height ?? window.innerHeight));
            const activeElement = document.activeElement;
            const hasFocusedField = activeElement instanceof HTMLElement
                && !!activeElement.closest("[data-quick-contact-form]")
                && activeElement.matches("input, textarea, select");
            const keyboardOffset = hasFocusedField && visualViewport
                ? Math.max(0, Math.round(window.innerHeight - visualViewport.height - visualViewport.offsetTop))
                : 0;

            document.documentElement.style.setProperty("--quick-vv-h", `${viewportHeight}px`);
            document.documentElement.style.setProperty("--quick-vv-bottom", `${keyboardOffset}px`);

            const target = mobileQuery.matches
                ? (isOpen() ? panel : openButton)
                : null;
            document.documentElement.style.setProperty("--quick-bar-h", `${target?.offsetHeight ?? bar.offsetHeight}px`);
        };

        const setOpen = (nextOpen: boolean, restoreFocus = false): void => {
            bar.classList.toggle("is-open", nextOpen);
            document.documentElement.classList.toggle("is-quick-contact-open", nextOpen && mobileQuery.matches);
            openButton?.setAttribute("aria-expanded", nextOpen ? "true" : "false");
            panel?.setAttribute("aria-hidden", nextOpen ? "false" : "true");
            updateViewportMetrics();

            if (nextOpen) {
                // iOS Safari는 16px 미만 input을 강제 focus하면 페이지를 확대하고 입력 위치로
                // visual viewport를 이동한다. sheet를 연 직후에는 키보드를 띄우지 않고,
                // 항상 보이는 닫기 버튼으로 focus를 옮긴다.
                window.setTimeout(() => closeButton?.focus({ preventScroll: true }), 180);
            } else if (restoreFocus) {
                openButton?.focus();
            }
        };

        updateViewportMetrics();
        window.addEventListener("resize", updateViewportMetrics, { passive: true });
        window.visualViewport?.addEventListener("resize", updateViewportMetrics, { passive: true });
        window.visualViewport?.addEventListener("scroll", updateViewportMetrics, { passive: true });
        mobileQuery.addEventListener("change", () => {
            if (!mobileQuery.matches) {
                setOpen(false);
                panel?.removeAttribute("aria-hidden");
                return;
            }
            panel?.setAttribute("aria-hidden", "true");
            updateViewportMetrics();
        });

        if (mobileQuery.matches) {
            panel?.setAttribute("aria-hidden", "true");
        }

        openButton?.addEventListener("click", () => setOpen(true));
        closeButton?.addEventListener("click", () => setOpen(false, true));
        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && mobileQuery.matches && isOpen()) {
                setOpen(false, true);
            }
        });

        const form = bar.querySelector<HTMLFormElement>("[data-quick-contact-form]");
        if (!form) return;

        form.addEventListener("focusin", updateViewportMetrics);
        form.addEventListener("focusout", () => window.setTimeout(updateViewportMetrics, 0));

        new Form(form, {
            submittingText: "전송 중...",
            validate: (f) => {
                const val = (sel: string): string =>
                    (f.querySelector<HTMLInputElement>(sel)?.value ?? "").trim();
                const checks: Array<[string, string, () => boolean]> = [
                    ["#quick_name", "이름을 입력해 주세요.", () => val("#quick_name") !== ""],
                    ["#quick_phone", "연락처를 입력해 주세요.", () => val("#quick_phone") !== ""],
                    ["#quick_phone", "정상적인 연락처를 입력해 주세요.", () => PHONE_PATTERN.test(val("#quick_phone"))],
                    ["#quick_contents", "문의 내용을 입력해 주세요.", () => val("#quick_contents") !== ""],
                    [
                        'input[name="privacy_agree"]',
                        "개인정보 수집·이용에 동의해 주세요.",
                        () => !!f.querySelector<HTMLInputElement>('input[name="privacy_agree"]')?.checked,
                    ],
                ];
                for (const [selector, msg, ok] of checks) {
                    if (!ok()) {
                        f.querySelector<HTMLElement>(selector)?.focus();
                        return msg;
                    }
                }
                return null;
            },
            onSuccess: async (data) => {
                const msg = normalizeFormResponse(data).message || "문의가 정상적으로 접수되었습니다. 확인 후 연락드리겠습니다.";
                form.reset();
                if (mobileQuery.matches) {
                    setOpen(false);
                }
                Toast.success(msg, { durationMs: 5500 });
            },
            onFail: (message) => {
                Toast.error(message || "접수 중 오류가 발생했습니다.");
            },
            requestFailedMessage: "네트워크 오류로 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        }).init();
    }
}
